import { useQuery } from "@apollo/client"
import React, { useState } from "react"
import StaticSelector from "components/Atoms/StaticSelector"
import StringInput from "components/Atoms/StringInput"
import ButtonMutation from "components/Molecules/ButtonMutation"
import DynamicSelector from "components/Molecules/DynamicSelector"
import FormController from "components/Molecules/FormController"
import AppToaster from "utils/AppToaster"
import { TRANSACTION_TYPES } from "data/static.items"
import { PAYMENT_TYPES } from "graphql/selector.queries.gql"
import { CONTRACT_FRAG } from "graphql/vehicle-management/vehicle-management.fragments.gql"
import { ADD_VEHICLE_PAYMENT } from "graphql/vehicle-management/vehicle-management.mutations.gql"
import { CONTRACT_PAYMENTS } from "graphql/vehicle-management/vehicle-management.queries.gql"
import { getErrors } from "hooks/useErrors"
import paymentModel from "models/payment.model"
import { getApolloId, getId } from "utils/custom.utils"
import PaymentRecords from "./payments/PaymentRecords"
import PaymentSummary from "./payments/PaymentSummary"
const VehiclePayments = ({ vehicle }) => {
  const contract = vehicle?.currentContract
  const contractId = contract?.id
  const driverOwner = getId(vehicle?.owner) === getId(vehicle?.driver)

  const { data, loading } = useQuery(CONTRACT_PAYMENTS, {
    variables: { contractId },
  })
  const [payment, setPayment] = useState({
    amount: 0,
    paidToId: driverOwner
      ? {
          id: vehicle?.owner?.id,
          name: `Owner & Driver - ${vehicle?.owner?.fullName}`,
        }
      : {
          id: vehicle?.owner?.id,
          name: `Owner - ${vehicle?.owner?.fullName}`,
        },
    remark: "",
    transactionType: "",
    paymentTypeId: null,
  })

  function handleChange(path, value) {
    setPayment(prev => ({
      ...prev,
      [path]: value,
    }))
  }
  const payments = data?.contractPayments ?? []
  const totalPaid = payments
    ? Math.round(payments.reduce((prev, payment) => prev + payment?.amount, 0))
    : 0

  return (
    <div className="vt-vehicle-payments">
      <FormController source={payment} onChange={handleChange}>
        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-4">
            <DynamicSelector
              required
              label="Payment Type"
              query={PAYMENT_TYPES}
              path="paymentTypeId"
            />
          </div>
          <div className="col-span-4">
            <StringInput
              className="vt-payment"
              path="amount"
              fill
              label="Amount"
              big={false}
            />
          </div>
          <div className="col-span-4">
            <StaticSelector
              label="Paid To"
              path="paidToId"
              items={
                driverOwner
                  ? [
                      {
                        id: vehicle?.owner?.id,
                        name: `Owner & Driver - ${vehicle?.owner?.fullName}`,
                      },
                    ]
                  : [
                      {
                        id: vehicle?.owner?.id,
                        name: `Owner - ${vehicle?.owner?.fullName}`,
                      },
                      {
                        id: vehicle?.driver?.id,
                        name: `Driver - ${vehicle?.driver?.fullName}`,
                      },
                    ]
              }
            />
          </div>
        </div>
        <div className="grid grid-cols-12 gap-4 mt-3">
          <div className="col-span-4">
            <StaticSelector
              required
              label="Payment Method"
              items={TRANSACTION_TYPES}
              path="transactionType"
            />
          </div>
          <div className="col-span-8">
            <StringInput fill big={false} label="Remark" path="remark" />
          </div>
        </div>
      </FormController>

      <div className="flex justify-end mb-3">
        <ButtonMutation
          refetchQueries={[
            {
              query: CONTRACT_PAYMENTS,
              variables: {
                contractId,
              },
            },
          ]}
          onCompleted={data => {
            AppToaster.show({
              message: `${payment.paymentTypeId?.name} for ${vehicle?.registrationNumber} added successfully`,
              intent: "success",
              icon: "tick",
            })
          }}
          update={(cache, result) => {
            const payment = result?.data.addVehiclePayment.payment
            const contract = cache.readFragment({
              id: getApolloId(payment?.contract),
              fragment: CONTRACT_FRAG,
            })

            cache.writeFragment({
              id: getApolloId(payment?.contract),
              fragment: CONTRACT_FRAG,
              data: {
                ...contract,
                totalOutstanding: contract?.totalOutstanding - payment?.amount,
              },
            })
          }}
          onError={err => {
            AppToaster.show({
              message: "Unable to add a payment, please try again",
              intent: "danger",
            })
          }}
          validate={state => {
            const errors = getErrors({
              schema: paymentModel,
              state,
              abortEarly: true,
            })

            if (errors.latest) {
              AppToaster.show({
                message: errors.latest,
                intent: "danger",
                icon: "cross",
              })
              return false
            }
            return true
          }}
          icon="confirm"
          text="Add Payment"
          variables={{
            ...payment,
            paidToId: payment.paidToId?.id,
            transactionType: payment.transactionType?.id,
            contractId,
            paymentTypeId: payment?.paymentTypeId?.id,
            name: `${payment.paymentTypeId?.name} for ${vehicle?.registrationNumber}`,
            paymentFor: vehicle?.id,
          }}
          mutation={ADD_VEHICLE_PAYMENT}
        />
      </div>
      <PaymentSummary paid={totalPaid} due={contract?.totalOutstanding ?? 0} />
      <PaymentRecords contracts={payments} loading={loading} />
    </div>
  )
}

export default VehiclePayments
